import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ChevronLeftIcon, PlusIcon } from "lucide-react";
import { getTestBlueprint } from "@brewbase/lib";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { Button } from "@/components/ui/button";
import { RequireCapability } from "@/components/require-capability";
import NewTestTemplateDialog from "@/features/harvest-new-test-template-dialog";
import { BlueprintView } from "@/features/test-template/blueprint-view";

export const Route = createFileRoute("/analytics/blueprints/$blueprint_id")({
  loader: ({ params }) => {
    const blueprint = getTestBlueprint(params.blueprint_id);
    if (!blueprint) throw notFound();
    return { blueprint };
  },
  component: () => (
    <RequireCapability cap="test-template:edit">
      <RouteComponent />
    </RequireCapability>
  ),
});

/** A blueprint on its own: what it generates, and the methods made from it. */
function RouteComponent() {
  const { blueprint } = Route.useLoaderData();
  const canEdit = useCan("test-template:edit");
  const { data: methods } = trpc.testBlueprints.methods.useQuery({ blueprint: blueprint.id });
  return (
    <div className="flex flex-col gap-8 p-4">
      <Link
        to="/analytics/blueprints"
        className="flex items-center gap-1 text-xs text-olive-11 hover:underline"
      >
        <ChevronLeftIcon className="size-3" />
        Blueprints
      </Link>
      <div className="flex max-w-5xl flex-col gap-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-olive-11">Methods</p>
          {canEdit && (
            <NewTestTemplateDialog
              startFrom={blueprint.id}
              trigger={
                <Button variant="ghost" size="sm">
                  <PlusIcon className="h-4 w-4" />
                  New method
                </Button>
              }
            />
          )}
        </div>
        {methods && methods.length > 0 ? (
          <table className="w-full border border-olive-4 text-sm">
            <thead>
              <tr className="border-b border-olive-4 text-left text-xs text-olive-9">
                <th className="px-3 py-1 font-normal">Method</th>
                <th className="px-3 py-1 font-normal">Code</th>
                <th className="px-3 py-1 font-normal text-right">Compounds</th>
                <th className="px-3 py-1 font-normal text-right">Blueprint version</th>
              </tr>
            </thead>
            <tbody>
              {methods.map((m) => (
                <tr key={m.id} className="border-b border-olive-4 last:border-b-0 hover:bg-olive-2">
                  <td className="px-3 py-1.5">
                    <Link
                      to="/analytics/templates/$template_id"
                      params={{ template_id: m.id }}
                      className="underline-offset-2 hover:underline"
                    >
                      {m.name}
                    </Link>
                    {m.archived_at && <span className="ml-2 text-xs text-olive-9">archived</span>}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">{m.code}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{m.compound_count}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    v{m.blueprint_version}
                    {(m.blueprint_version ?? 0) < blueprint.version && (
                      <span className="ml-1 text-olive-9">(older)</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="py-1 text-xs text-olive-9">No methods use this blueprint yet.</p>
        )}
      </div>
      <BlueprintView blueprint={blueprint} />
    </div>
  );
}
